"use client";

import { createContext, useContext } from "react";

import type { TrackingScopeValue } from "@/contexts/trackingScopeContext";
import type { AddToListsTarget } from "@/lib/add-to-lists-target";

export interface ListMeta {
  id: string;
  name: string;
  type: string;
  icon: string | null;
  itemCount: number;
}

export type ItemQuery = { workId?: string | null; releaseId?: string | null };
export type PreferredListType = "collection" | "wishlist";
export interface OpenAddToListsParams {
  preferredListType?: PreferredListType;
  target: AddToListsTarget;
  trackingScope?: TrackingScopeValue;
}

export interface ListsContextValue {
  allLists: ListMeta[];

  /** Returns all lists containing the item (by releaseId first, falls back to workId). */
  getListsForItem: (params: ItemQuery) => ListMeta[];
  /** Convenience boolean: is the item in the user's collection? */
  isInCollection: (params: ItemQuery) => boolean;
  /** Convenience boolean: is the item in the user's wishlist? */
  isInWishlist: (params: ItemQuery) => boolean;

  /** Per-release lookup: which list IDs contain this release? */
  getListIdsForRelease: (releaseId: string) => Set<string>;

  openAddToLists: (params: OpenAddToListsParams) => void;
  isLoaded: boolean;

  wizardOpen: boolean;
  closeWizard: () => void;
}

const EMPTY_SET = new Set<string>();
const EMPTY_ARRAY: ListMeta[] = [];

export const ListsContext = createContext<ListsContextValue>({
  allLists: [],
  getListsForItem: () => EMPTY_ARRAY,
  isInCollection: () => false,
  isInWishlist: () => false,
  getListIdsForRelease: () => EMPTY_SET,
  openAddToLists: () => {},
  isLoaded: false,

  wizardOpen: false,
  closeWizard: () => {},
});

export function useLists() {
  return useContext(ListsContext);
}
